import { AuthManager } from '@store/AuthManager';
import { getConfig } from '@utils/config';
import { f } from '@utils/nextForms';
import { IS_NODE, formDataEncoder } from '@utils/types';
import {
  ZodiosError,
  prefixApi,
  type ZodiosPlugin,
  ZodiosInstance,
  parametersBuilder,
} from '@zodios/core';
import { Zodios } from '@zodios/core';
import { ZodiosEndpointDefinitions } from '@zodios/core';
import { ZodiosHooksInstance } from '@zodios/react';
import { ZodiosHooks } from '@zodios/react';
import Axios, { AxiosRequestConfig, isAxiosError } from 'axios';
import chalk from 'chalk';
import type { ZodType, z } from 'zod';
import { ZodObject } from 'zod';

declare module 'axios' {
  export interface AxiosRequestConfig {
    logApiMetadata?: {
      start: number;
    };
  }
}

export const logApiPlugin = (): ZodiosPlugin => {
  return {
    async request(_, config) {
      (config as AxiosRequestConfig).logApiMetadata = {
        start: performance.now(),
      };

      return config;
    },
    async response(api, config, response) {
      const now = (config.logApiMetadata?.start! / 1000).toFixed(1);

      console.info(
        chalk.bgBlackBright(
          `[${now}s +${(performance.now() - config.logApiMetadata?.start!).toFixed(0)}ms]`.padEnd(
            16,
          ),
        ),
        chalk.cyan(config.method.toUpperCase().padEnd(6)),
        config.url,
      );

      return response;
    },
  };
};

export const jwtRefreshApiPlugin = (): ZodiosPlugin => {
  return {
    async request(_, config) {
      await AuthManager.shared.checkTokenRefresh();
      return config;
    },
  };
};

/**
 *
 * @param s the status you want to convert
 * @param d the target data
 * @returns a plugin that makes the response `d` when the http status is `s`.
 * Useful when you want to ignore 404 errors.
 */
export const httpStatusAsData = (s: number, d: any): ZodiosPlugin => {
  return {
    async request(_, config) {
      return {
        ...config,
        validateStatus(status) {
          return (status >= 200 && status < 400) || status === s;
        },
      };
    },
    async response(api, config, response) {
      if (response.status === s) {
        response.data = d;
      }
      return response;
    },
  };
};

/**
 * @returns a plugin that transforms the response to `Blob`, when response tpye is `f.instanceOfBlob`
 */
export const blobPlugin = (): ZodiosPlugin => {
  return {
    name: 'blob',
    request: async (api, config) => {
      const endpoint = api.find((e) => e.method === config.method && e.path === config.url);

      if (!endpoint || !f.isInstanceOfBlob(endpoint.response)) return config;

      return {
        ...config,
        responseType: 'blob',
      };
    },
  };
};

/**
 * @returns a plugin that extracts inner query object.
 * The object has to be placed under a `Query` type param named `query`,
 * as a workaround to use schema definition for query params in Zodios.
 */
export const queryObjectPlugin = (): ZodiosPlugin => {
  return {
    name: 'query',
    request: async (api, config) => {
      if (config.method === 'get') {
        const endpoint = api.find((e) => e.method === config.method && e.path === config.url);
        const querySchema = endpoint?.parameters?.find(
          (p) => p.type === 'Query' && p.name === 'query',
        )?.schema;

        return querySchema instanceof ZodObject
          ? {
              ...config,
              queries: config.queries?.query as any,
            }
          : config;
      }
      return config;
    },
  };
};

/**
 *
 * @returns a plugin that encodes into `multipart/form-data` using `formDataEncoder`.
 * Useful if you have both binary (file) and text parameters in the request.
 * (Although URL by file uploading is recommended)
 */
export const formDataPlugin = (): ZodiosPlugin => {
  return {
    name: 'form-data',
    request: async (_, config) => {
      if (typeof config.data !== 'object' || Array.isArray(config.data)) {
        throw new ZodiosError('Zodios: multipart/form-data body must be an object', config as any);
      }
      const result = formDataEncoder.encode(config.data ?? new FormData());

      return {
        ...config,
        data: result,
        headers: {
          ...config.headers,
          'Content-Type': 'multipart/form-data',
        },
      };
    },
  };
};

// Create an axios instance that appends '/' to every request
// This is required for Django.
export const createAxiosInstance = () => {
  const axiosInstance = Axios.create({
    paramsSerializer: {
      indexes: null,
    },
  });

  axiosInstance.interceptors.request.use((r) => {
    if (AuthManager.shared.key) {
      r.headers.set('Authorization', `${AuthManager.shared.auth_type} ${AuthManager.shared.key}`);
    }

    if (AuthManager.shared.impersonatedUser) {
      r.headers.set('Impersonate-User-Id', `${AuthManager.shared.impersonatedUser}`);
    }

    if (AuthManager.shared.originalUser) {
      r.headers.set('Impersonate-Original-User-Id', `${AuthManager.shared.originalUser}`);
    }

    if (r.url?.endsWith('/')) {
      return r;
    } else {
      return {
        ...r,
        url: r.url && `${r.url}/`,
        __originalUrl: r.url,
      };
    }
  });

  axiosInstance.interceptors.response.use(
    (r) => {
      /**
       * For consistency, we also restore the URL for successful responses.
       */
      r.config.url = (r.config as any).__originalUrl;
      return r;
    },
    (e) => {
      if (isAxiosError(e) && !e.response && e.code === 'ERR_NETWORK') {
        if (!window.location.pathname.startsWith('/auth/')) {
          AuthManager.shared.clear();
          window.location.href = '/auth/Login';
          return new Promise(() => {});
        }
      }
      if (isAxiosError(e) && e.config) {
        /**
         * This is ugly, but necessary, because in `isErrorFromPath`, zodios will compare `config.url` against the URL in the schema.
         * If the `e.config.url` is appended in the request interceptor, the comparison will fail.
         */
        e.config.url = (e.config as any).__originalUrl;
      }
      return Promise.reject(e);
    },
  );

  return axiosInstance;
};

export const schemaToQueryParameters = <T extends z.ZodObject<z.ZodRawShape>>(t: T) => {
  return parametersBuilder().addQueries<T['shape']>(t.shape).build();
};

type CleanPath<Path extends string> = Path extends `${infer PClean}/` ? PClean : Path;

type MapApiPath<Path extends string, Api, Acc extends unknown[] = []> = Api extends readonly [
  infer Head,
  ...infer Tail,
]
  ? MapApiPath<
      Path,
      Tail,
      [
        ...Acc,
        {
          [K in keyof Head]: K extends 'path'
            ? Head[K] extends string
              ? CleanPath<`${Path}${Head[K]}`>
              : Head[K]
            : Head[K];
        },
      ]
    >
  : Acc;

type Try<A, B, C> = A extends B ? A : C;
type NarrowRaw<T> =
  | (T extends Function ? T : never)
  | (T extends string | number | bigint | boolean ? T : never)
  | (T extends [] ? [] : never)
  | {
      [K in keyof T]: K extends 'description' ? T[K] : NarrowNotZod<T[K]>;
    };
type NarrowNotZod<T> = Try<T, ZodType, NarrowRaw<T>>;
/**
 * Utility to infer the embedded primitive type of any type
 * Same as `as const` but without setting the object as readonly and without needing the user to use it
 * @param T - type to infer the embedded type of
 * @see - thank you tannerlinsley for this idea
 */
type Narrow<T> = Try<T, [], NarrowNotZod<T>>;

export type CreateApiHooks<
  Api extends ZodiosEndpointDefinitions,
  Prefix extends string,
  MappedApi extends ZodiosEndpointDefinitions = MapApiPath<Prefix, Api, []>,
> = ZodiosHooksInstance<MappedApi> & { client: ZodiosInstance<MappedApi> };

export const createApiHooks = <Api extends ZodiosEndpointDefinitions, Prefix extends string>(
  prefix: Prefix,
  api: Narrow<Api>,
): CreateApiHooks<Api, Prefix> => {
  const zodiosClient = new Zodios(getConfig().serverUrl, prefixApi(prefix, api) as any, {
    axiosInstance: createAxiosInstance(),
  }) as any;

  zodiosClient.use(blobPlugin());
  zodiosClient.use(queryObjectPlugin());
  zodiosClient.use(jwtRefreshApiPlugin());

  if (IS_NODE) {
    zodiosClient.use(logApiPlugin());
  }

  const hooks = new ZodiosHooks(prefix, zodiosClient) as any;
  hooks.client = zodiosClient;
  return hooks;
};
